/**
 * About Section - Evolution story and current focus
 *
 * Content from: about-us-raja-content.md (Landing Page Section 2)
 */

import React, { useState } from 'react';

const profileImages = [
  { src: '/raja-profile.png', alt: 'Raja Sleem' },
  { src: '/raja-profile-2.png', alt: 'Raja Sleem - Professional' },
];

function About() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () =>
    setCurrentIndex((prev) => (prev === 0 ? profileImages.length - 1 : prev - 1));
  const goToNext = () =>
    setCurrentIndex((prev) => (prev === profileImages.length - 1 ? 0 : prev + 1));

  return (
    <section id="about" className="py-8 px-6 bg-white dark:bg-slate-900 transition-colors">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl font-bold font-heading text-slate-900 dark:text-slate-50 mb-8">
          About
        </h2>

        <div className="flex flex-col md:flex-row gap-8 items-start">
          {/* Profile Photo Carousel - toggle between two photos */}
          <div className="flex-shrink-0">
            <div className="relative">
              <button
                onClick={goToPrevious}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-8 h-8 flex items-center justify-center rounded-full bg-white dark:bg-slate-700 shadow-md hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors z-10"
                aria-label="Previous photo"
              >
                <svg className="w-4 h-4 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <img
                src={profileImages[currentIndex].src}
                alt={profileImages[currentIndex].alt}
                className="w-48 h-48 rounded-2xl object-cover border-2 border-slate-200 dark:border-slate-700 shadow-lg"
              />

              <button
                onClick={goToNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-8 h-8 flex items-center justify-center rounded-full bg-white dark:bg-slate-700 shadow-md hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors z-10"
                aria-label="Next photo"
              >
                <svg className="w-4 h-4 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Dots indicator */}
            <div className="flex justify-center gap-2 mt-3">
              {profileImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentIndex
                      ? 'bg-sky-500'
                      : 'bg-slate-300 dark:bg-slate-600 hover:bg-slate-400 dark:hover:bg-slate-500'
                  }`}
                  aria-label={`Go to photo ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Bio Text */}
          <div className="space-y-6 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
            <p>
              I've been a Software Engineer since 2021, but my journey in tech spans 10+ years.
              Software automation. DevOps infrastructure. Kubernetes clusters. Backend engineering.
              Each shift taught me to adapt fast and think in systems, not just code.
            </p>

            <p>
              Now I'm focused on what's next: AI-augmented development. Using LLM
              tools like Claude Code and GitHub Copilot to navigate complex codebases,
              debug faster, and build smarter. The engineers who embrace this will
              outpace those who don't.
            </p>

            <p className="text-slate-500 dark:text-slate-400 italic">
              My joke: "My next programming language is markdown."
            </p>
          </div>
        </div>

        {/* Philosophy - Programming Evolution */}
        <div className="mt-8 p-6 bg-slate-50 dark:bg-slate-800 rounded-xl transition-colors">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">
            The Evolution of Programming
          </h3>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-300 mb-4">
            <span className="px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded">Assembly</span>
            <span className="text-slate-400">→</span>
            <span className="px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded">C</span>
            <span className="text-slate-400">→</span>
            <span className="px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded">Java/Python</span>
            <span className="text-slate-400">→</span>
            <span className="px-2 py-1 bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-300 rounded font-medium">Natural Language (AI)</span>
          </div>
          <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
            Each level abstracts away lower-level details. LLM-augmented development
            is the next natural step. Coding isn't about syntax anymore, it's about
            problem-solving, architecture, and validation.
          </p>
        </div>

        {/* Career arc visual */}
        <div className="mt-8 p-6 bg-slate-50 dark:bg-slate-800 rounded-xl transition-colors">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">
            My Evolution
          </h3>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <span className="px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded">Software Automation</span>
            <span className="text-slate-400">→</span>
            <span className="px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded">DevOps</span>
            <span className="text-slate-400">→</span>
            <span className="px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded">Backend Engineering</span>
            <span className="text-slate-400">→</span>
            <span className="px-2 py-1 bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-300 rounded font-medium">AI-Augmented</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default About;
